/**
 * Dashboard boot performance trace.
 *
 * Stitches together everything that happens between the first byte and the
 * dashboard's first useful paint into one timeline, so a slow load can be
 * diagnosed from a single object instead of guessed at:
 *
 *   - navigation timing (TTFB, DOMContentLoaded, load)
 *   - paint metrics: FCP, LCP (with the element that painted it), and CLS with
 *     the elements that shifted
 *   - every `bootMetrics.mark()` — prefetch lifecycle, auth/org resolution,
 *     skeleton stages, per-section first content, per-query data arrival
 *   - every API request the page made (fetch/XHR resource timing, query strings
 *     stripped), including the quickfetch call's `Server-Timing` breakdown of
 *     the backend workers
 *
 * Marks and observers are always on — they cost microseconds. What the flag
 * (perfFlag.ts) gates is the OUTPUT: with `ahoy:perf` on, the finished trace
 * is written to the console and uploaded to Sentry as a "dashboard perf trace"
 * event; with it off, the trace is only attached as Sentry context so it rides
 * along with any error the page happens to report.
 *
 * In production: ask the user to open the dashboard with `?ahoy_perf=1`, then
 * read the trace from Sentry — or have them run `window.__ahoy.perf.dump()`.
 */

import * as Sentry from '@sentry/react';

import { type BootMark, getMarks, onMark } from '@/lib/boot/bootMetrics';
import { createLogger } from '@/lib/logger';

import {
  disablePerfTrace,
  enablePerfTrace,
  isPerfTraceEnabled,
  isPerfUploadThrottled,
  markPerfUploadDone,
} from './perfFlag';

const perfLogger = createLogger({ module: 'perf' });

/** Longest we wait for `dashboard-ready` before finalizing the trace anyway.
 *  Slow loads are the ones we care about, so this is generous. */
export const PERF_TRACE_FINALIZE_TIMEOUT_MS = 20_000;

/** Cap on resource entries kept — the boot path is a few dozen requests. */
const MAX_RESOURCES = 80;

/** Cap on individual layout-shift records kept (CLS total is exact). */
const MAX_SHIFTS = 30;

/** Resource initiator types that correspond to API traffic. */
const API_INITIATOR_TYPES: ReadonlySet<string> = new Set([
  'fetch',
  'xmlhttprequest',
]);

const QUICKFETCH_PATH_FRAGMENT = '/quickfetch/dashboard/';

export type PerfTraceFinalizeReason =
  | 'dashboard-ready'
  | 'timeout'
  | 'pagehide'
  | 'manual';

export interface PerfResourceEntry {
  /** Origin + path only — query strings are stripped (they may carry tokens). */
  name: string;
  startMs: number;
  durationMs: number;
  transferBytes: number;
  /** `Server-Timing` metrics exposed by the backend (name → duration ms). */
  serverTiming: Record<string, number>;
}

export interface PerfLayoutShift {
  value: number;
  startMs: number;
  /** Structural descriptions of the shifted nodes — tag, id, data-component. */
  sources: string[];
}

export interface PerfTraceMark {
  name: string;
  tMs: number;
  data?: Record<string, unknown>;
}

export interface PerfTrace {
  finalizedAt: PerfTraceFinalizeReason;
  navigation: {
    ttfbMs: number;
    domContentLoadedMs: number;
    loadMs: number;
  } | null;
  paint: {
    fcpMs: number | null;
    lcpMs: number | null;
    lcpElement: string | null;
  };
  cls: {
    total: number;
    shifts: PerfLayoutShift[];
  };
  marks: PerfTraceMark[];
  resources: PerfResourceEntry[];
  quickfetch: PerfResourceEntry | null;
}

export interface PerfDevtools {
  enable(): void;
  disable(): void;
  dump(): PerfTrace;
}

// ---------------------------------------------------------------------------
// Observer state
// ---------------------------------------------------------------------------

interface LcpEntry extends PerformanceEntry {
  element?: Element | null;
}

interface LayoutShiftEntry extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
  sources?: Array<{ node?: Node | null }>;
}

let observersInstalled = false;
let lcp: { ms: number; element: string | null } | null = null;
let fcpMs: number | null = null;
let clsTotal = 0;
const shifts: PerfLayoutShift[] = [];

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

/** Describe a node structurally — never its text (could be customer data). */
function describeNode(node: Node | null | undefined): string {
  if (!node || !(node instanceof Element)) {
    return node ? node.nodeName.toLowerCase() : 'unknown';
  }
  const tag = node.tagName.toLowerCase();
  const id = node.id ? `#${node.id}` : '';
  const component = node.getAttribute('data-component');
  const componentPart = component ? `[data-component=${component}]` : '';
  const testId = node.getAttribute('data-testid');
  const testIdPart = !component && testId ? `[data-testid=${testId}]` : '';
  return `${tag}${id}${componentPart}${testIdPart}`;
}

function observe(
  type: string,
  onEntries: (entries: PerformanceEntryList) => void
): void {
  try {
    const observer = new PerformanceObserver(list =>
      onEntries(list.getEntries())
    );
    observer.observe({ type, buffered: true });
  } catch {
    // Entry type unsupported in this browser — that metric stays null.
  }
}

/** Start the paint/layout observers. Idempotent; safe to call before mount. */
export function installPerfObservers(): void {
  if (observersInstalled || typeof PerformanceObserver === 'undefined') {
    return;
  }
  observersInstalled = true;

  observe('largest-contentful-paint', entries => {
    for (const entry of entries as LcpEntry[]) {
      lcp = {
        ms: round(entry.startTime),
        element: describeNode(entry.element),
      };
    }
  });

  observe('paint', entries => {
    for (const entry of entries) {
      if (entry.name === 'first-contentful-paint') {
        fcpMs = round(entry.startTime);
      }
    }
  });

  observe('layout-shift', entries => {
    for (const entry of entries as LayoutShiftEntry[]) {
      if (entry.hadRecentInput) {
        continue;
      }
      clsTotal += entry.value;
      if (shifts.length < MAX_SHIFTS) {
        shifts.push({
          value: round(entry.value * 1000) / 1000,
          startMs: round(entry.startTime),
          sources: (entry.sources ?? []).map(source =>
            describeNode(source.node)
          ),
        });
      }
    }
  });
}

// ---------------------------------------------------------------------------
// Trace assembly
// ---------------------------------------------------------------------------

function stripQuery(url: string): string {
  try {
    const parsed = new URL(url, window.location.href);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url.split('?')[0];
  }
}

function collectResources(): PerfResourceEntry[] {
  const entries = performance.getEntriesByType(
    'resource'
  ) as PerformanceResourceTiming[];
  return entries
    .filter(entry => API_INITIATOR_TYPES.has(entry.initiatorType))
    .sort((a, b) => a.startTime - b.startTime)
    .slice(0, MAX_RESOURCES)
    .map(entry => ({
      name: stripQuery(entry.name),
      startMs: round(entry.startTime),
      durationMs: round(entry.duration),
      transferBytes: entry.transferSize,
      serverTiming: Object.fromEntries(
        (entry.serverTiming ?? []).map(metric => [
          metric.name,
          round(metric.duration),
        ])
      ),
    }));
}

function collectNavigation(): PerfTrace['navigation'] {
  const [nav] = performance.getEntriesByType(
    'navigation'
  ) as PerformanceNavigationTiming[];
  if (!nav) {
    return null;
  }
  return {
    ttfbMs: round(nav.responseStart),
    domContentLoadedMs: round(nav.domContentLoadedEventEnd),
    loadMs: round(nav.loadEventEnd),
  };
}

function toTraceMark(mark: BootMark): PerfTraceMark {
  const { name, t, ...data } = mark;
  const traceMark: PerfTraceMark = { name, tMs: round(t) };
  if (Object.keys(data).length > 0) {
    traceMark.data = data;
  }
  return traceMark;
}

/** Assemble the trace from everything recorded so far. Pure read. */
export function buildPerfTrace(
  finalizedAt: PerfTraceFinalizeReason
): PerfTrace {
  const resources = collectResources();
  return {
    finalizedAt,
    navigation: collectNavigation(),
    paint: {
      fcpMs,
      lcpMs: lcp?.ms ?? null,
      lcpElement: lcp?.element ?? null,
    },
    cls: { total: round(clsTotal * 1000) / 1000, shifts: [...shifts] },
    marks: getMarks().map(toTraceMark),
    resources,
    quickfetch:
      resources.find(resource =>
        resource.name.includes(QUICKFETCH_PATH_FRAGMENT)
      ) ?? null,
  };
}

interface TimelineRow {
  tMs: number;
  kind: 'mark' | 'request';
  name: string;
  detail: string;
}

/** Marks and requests interleaved by time — the human-readable waterfall. */
export function buildTimeline(trace: PerfTrace): TimelineRow[] {
  const rows: TimelineRow[] = [];
  for (const mark of trace.marks) {
    rows.push({
      tMs: mark.tMs,
      kind: 'mark',
      name: mark.name,
      detail: mark.data ? JSON.stringify(mark.data) : '',
    });
  }
  for (const resource of trace.resources) {
    const serverTiming = Object.entries(resource.serverTiming)
      .map(([name, ms]) => `${name}=${ms}`)
      .join(' ');
    rows.push({
      tMs: resource.startMs,
      kind: 'request',
      name: resource.name,
      detail: `${resource.durationMs}ms${serverTiming ? ` [${serverTiming}]` : ''}`,
    });
  }
  return rows.sort((a, b) => a.tMs - b.tMs);
}

function logTrace(trace: PerfTrace): void {
  // console.warn is the sanctioned console channel (no-console allows warn/error).
  console.warn('[ahoy:perf] dashboard boot timeline', buildTimeline(trace));
  console.warn('[ahoy:perf] dashboard boot trace', trace);
}

/** Compact summary attached to every Sentry event as context. */
function toSentryContext(trace: PerfTrace): Record<string, unknown> {
  const stageMarks = Object.fromEntries(
    trace.marks
      .filter(
        mark =>
          /^(stage|section|data|quickfetch|prefetch|auth):/.test(mark.name) ||
          mark.name === 'dashboard-ready'
      )
      .map(mark => [mark.name, mark.tMs])
  );
  return {
    finalizedAt: trace.finalizedAt,
    ttfbMs: trace.navigation?.ttfbMs ?? null,
    fcpMs: trace.paint.fcpMs,
    lcpMs: trace.paint.lcpMs,
    lcpElement: trace.paint.lcpElement,
    cls: trace.cls.total,
    requestCount: trace.resources.length,
    quickfetchMs: trace.quickfetch?.durationMs ?? null,
    quickfetchServerTiming: trace.quickfetch?.serverTiming ?? null,
    ...stageMarks,
  };
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

let finalized = false;
let finalizeTimer: ReturnType<typeof setTimeout> | null = null;
let stopListeningForReady: (() => void) | null = null;

function finalize(reason: PerfTraceFinalizeReason): PerfTrace {
  const trace = buildPerfTrace(reason);
  if (finalized) {
    return trace;
  }
  finalized = true;
  if (finalizeTimer !== null) {
    clearTimeout(finalizeTimer);
    finalizeTimer = null;
  }
  stopListeningForReady?.();
  stopListeningForReady = null;

  Sentry.setContext('dashboard_perf', toSentryContext(trace));

  if (isPerfTraceEnabled()) {
    logTrace(trace);
    if (!isPerfUploadThrottled()) {
      Sentry.captureMessage('dashboard perf trace', {
        level: 'info',
        tags: { 'perf.trace': reason },
        extra: { trace },
      });
      markPerfUploadDone();
      perfLogger.info('Dashboard perf trace uploaded to Sentry', { reason });
    } else {
      perfLogger.info(
        'Dashboard perf trace throttled — tab session already uploaded',
        { reason }
      );
    }
  }
  return trace;
}

function dump(): PerfTrace {
  const trace = buildPerfTrace('manual');
  logTrace(trace);
  return trace;
}

function exposeDevtools(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const perf: PerfDevtools = {
    enable: () => {
      enablePerfTrace();
      console.warn(
        '[ahoy:perf] Perf tracing enabled. Reload to capture the boot sequence; ' +
          'the trace prints here and uploads to Sentry when the dashboard is ready.'
      );
    },
    disable: () => {
      disablePerfTrace();
      console.warn('[ahoy:perf] Perf tracing disabled.');
    },
    dump,
  };
  window.__ahoy = { ...(window.__ahoy ?? {}), perf };
}

/**
 * Wire the trace into the page: start observers, expose `window.__ahoy.perf`
 * (in every environment — production debugging is the point), and finalize
 * when the dashboard reports ready, when the user leaves, or after a timeout.
 * Call once from main.tsx.
 */
export function installPerfTrace(): void {
  installPerfObservers();
  exposeDevtools();

  stopListeningForReady = onMark(mark => {
    if (mark.name === 'dashboard-ready') {
      finalize('dashboard-ready');
    }
  });
  finalizeTimer = setTimeout(
    () => finalize('timeout'),
    PERF_TRACE_FINALIZE_TIMEOUT_MS
  );
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => finalize('pagehide'), {
      once: true,
    });
  }
}

/** Test-only — reset module state between tests. */
export function resetPerfTraceForTest(): void {
  finalized = false;
  if (finalizeTimer !== null) {
    clearTimeout(finalizeTimer);
    finalizeTimer = null;
  }
  stopListeningForReady?.();
  stopListeningForReady = null;
  observersInstalled = false;
  lcp = null;
  fcpMs = null;
  clsTotal = 0;
  shifts.length = 0;
}
